// Shared plumbing for the consent detectors (index.ts): where they read the page from, and the
// cookie lookup. Every detector takes an optional `ConsentSources` so tests (and the odd shop with a
// renamed global) can inject them; by default the real `window` / `document.cookie` are read LAZILY,
// on each call — never at import time, so the module loads under plain Node (`pnpm test`) and a
// banner that loads after us, or an answer given later in the visit, is still seen.
//
// No imports at all, so Node's type stripping loads it (`pnpm test`).

export interface ConsentSources {
  // The global object the CMP lives on (default: `window`).
  win?: unknown;
  // The raw `document.cookie` string, or a getter for it (default: `document.cookie`, read per call).
  cookie?: string | (() => string);
}

export const isObject = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;

// A CMP global whose properties we read, which may be a FUNCTION carrying them (CookieScript's
// `window.CookieScript` is its constructor, with `instance` as a property). Narrower than loosening
// `isObject`, which elsewhere also rejects functions where only a plain value makes sense.
export const isObjectOrFunction = (v: unknown): v is Record<string, unknown> =>
  (typeof v === "object" && v !== null) || typeof v === "function";

export const resolveWindow = (sources: ConsentSources | undefined): Record<string, unknown> => {
  const win = sources && "win" in sources ? sources.win : typeof window === "undefined" ? undefined : window;
  return isObject(win) ? win : {};
};

export const resolveCookie = (sources: ConsentSources | undefined): string => {
  const cookie = sources?.cookie ?? (() => (typeof document === "undefined" ? "" : document.cookie));
  const value = typeof cookie === "function" ? cookie() : cookie;
  return typeof value === "string" ? value : "";
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The raw (still URL-encoded) values of every cookie named `name`, in `document.cookie` order. The
// same name can appear more than once (set on several paths / domains, e.g. `www.shop.cz` and
// `.shop.cz`), and that order doesn't reliably say which copy the CMP itself reads — so callers
// judge all of them (`everyCookie`).
export const readCookies = (cookie: string, name: string): string[] =>
  Array.from(cookie.matchAll(new RegExp(`(?:^|;)\\s*${escapeRegExp(name)}=([^;]*)`, "g")), (m) => m[1] ?? "");

// Grants only when cookie `name` is present and EVERY copy of it grants (fail closed when a stale
// copy says otherwise).
export const everyCookie = (cookie: string, name: string, grants: (raw: string) => boolean): boolean => {
  const values = readCookies(cookie, name);
  return values.length > 0 && values.every(grants);
};

// decodeURIComponent that returns the input on a malformed escape instead of throwing.
export const safeDecode = (raw: string): string => {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

// Runs a detector body and turns a throw (a CMP global behind a throwing getter, a hostile
// `currentState()`, …) or any non-`true` result into "denied": detectors never throw, they fail closed.
export const failClosed = (read: () => boolean): boolean => {
  try {
    return read() === true;
  } catch {
    return false;
  }
};
